import { useState, useEffect, useCallback } from 'react';
import { ApiState, ApiError, ApiResponse, ApiStateError } from './types';
import { queryCache } from './cache';
import { Milliseconds } from 'types/units';

interface QueryOptions<T> {
  enabled?: boolean;
  cacheTime?: Milliseconds;
  retry?: number; // TODO: Implement retry logic
  retryDelay?: Milliseconds; // TODO: Implement retry logic
  onSuccess?: (data: T[]) => void; // TODO: Decide if we need the onSuccess and onError callbacks if we are using ApiState to communicate status and data
  onError?: (error: ApiError) => void;
}

interface MutationOptions<TData, TVariables> {
  onSuccess?: (data: TData, variables: TVariables) => void; // TODO: Decide if we need the onSuccess, onError, and onSettled callbacks if we are using ApiState to communicate status and data
  onError?: (error: ApiError, variables: TVariables) => void;
  onSettled?: (
    data: TData | undefined,
    error: ApiError | null,
    variables: TVariables
  ) => void;
}

export function useQuery<T>(
  key: string,
  fetcher: () => Promise<ApiResponse<T>>, // TODO: Change the name from fetcher to avoid confusion with fetch API (apiCall), // TODO: Create formalized return type
  options: QueryOptions<T> = {}
) {
  // TODO: Create Return Type
  const [state, setState] = useState<ApiState<T>>({ status: 'idle' });

  // Destructure options to avoid infinite re-renders
  const { enabled = true, cacheTime, onSuccess, onError } = options;

  const execute = useCallback(async () => {
    // TODO: Re-enable caching. Updating a grade name does not allow product grade to be updated because it's cache is not invalidated
    // const cached = queryCache.get<T>(key);
    // if (cached) {
    //   setState({ status: 'success', data: cached });
    //   return;
    // }

    setState({ status: 'loading' });

    try {
      const response = await fetcher();
      queryCache.set(key, response.data, cacheTime);
      setState({ status: 'success', data: response.data });
      onSuccess?.([response.data]);
    } catch (err) {
      const error = err as ApiError;
      const apiError: ApiStateError = {
        status: 'error',
        error: {
          message: error.message,
          code: error.code,
          details: error.response?.data,
        },
      };
      setState(apiError);
      onError?.(error);
    }
  }, [key, fetcher, cacheTime, onSuccess, onError]);

  useEffect(() => {
    if (enabled) {
      execute();
    }
  }, [execute, enabled]);

  return {
    ...state,
    refetch: execute,
    invalidate: () => queryCache.invalidate(key),
  };
}

export function useMutation<TData = unknown, TVariables = unknown>(
  mutationFn: (variables: TVariables) => Promise<ApiResponse<TData>>,
  options: MutationOptions<TData, TVariables> = {}
) {
  const [state, setState] = useState<ApiState<TData>>({ status: 'idle' });

  const mutate = async (variables: TVariables): Promise<TData> => {
    setState({ status: 'loading' });

    try {
      const response = await mutationFn(variables);
      setState({ status: 'success', data: response.data });
      options.onSuccess?.(response.data, variables);
      options.onSettled?.(response.data, null, variables);
      return response.data;
    } catch (err) {
      const error = err as ApiError;
      setState({
        status: 'error',
        error: {
          message: error.message,
          code: error.code,
          details: error.response?.data,
        },
      });
      options.onError?.(error, variables);
      options.onSettled?.(undefined, error, variables);
      throw error;
    }
  };

  return {
    ...state,
    mutate,
    reset: () => setState({ status: 'idle' }),
  };
}

interface InfiniteQueryOptions<T> extends QueryOptions<T> {
  getNextPageParam: (lastPage: T, pages: T[]) => unknown;
}

export function useInfiniteQuery<T>(
  key: string,
  fetcher: (pageParam: unknown) => Promise<ApiResponse<T>>,
  options: InfiniteQueryOptions<T>
) {
  const [pages, setPages] = useState<T[]>([]);
  const [state, setState] = useState<ApiState<T[]>>({ status: 'idle' });

  // TODO: Implement caching for infinite queries

  const { onSuccess, onError, getNextPageParam } = options;

  const fetchNextPage = useCallback(async () => {
    if (state.status === 'loading') return;

    setState({ status: 'loading' });

    try {
      const pageParam = pages.length
        ? getNextPageParam(pages[pages.length - 1], pages)
        : undefined;
      const response = await fetcher(pageParam);
      const newPages = [...pages, response.data];
      setPages(newPages);
      setState({ status: 'success', data: newPages });
      onSuccess?.(newPages);
    } catch (err) {
      const error = err as ApiError;
      setState({
        status: 'error',
        error: {
          message: error.message,
          code: error.code,
          details: error.response?.data,
        },
      });
      onError?.(error);
    }
  }, [state.status, pages, fetcher, onSuccess, onError, getNextPageParam]);

  useEffect(() => {
    if (options.enabled !== false) {
      fetchNextPage();
    }
  }, [options.enabled, fetchNextPage]);

  return {
    ...state,
    pages,
    fetchNextPage,
    hasNextPage:
      pages.length > 0 &&
      options.getNextPageParam(pages[pages.length - 1], pages) !== undefined,
    reset: () => {
      setPages([]);
      setState({ status: 'idle' });
    },
  };
}
